<!--
  RecordElement.vue
  A chip for a record: its name as a link, its icon, and a delete button.
  Shows the record row in `value`, with the `icon` and `color` it carries or, when
  it has none, the ones the catalog holds for its type (withAppearance in
  @auger/core/fields).

  Props:
    - value: The record row (required): name, type, slug, and icon / color
    - readonly: Hide delete button when true (default: false)
    - size: Chip size ('x-small', 'small', 'normal', 'medium', 'large')
    - showIcon: Show the record's icon (default: true)
    - fixed: Disable navigation link (default: false)
  Emits:
    - remove: Emitted when delete button is clicked with the record
-->
<template>
  <span v-if="value" :class="tagClass" :style="{ '--tag-color': color }">
    <span v-if="fixed" :class="textClass">
      <span class="font-semibold">{{ value.name }}</span>
    </span>
    <AugerLink v-else :target="recordTarget(value)" :class="textClass" @click.stop>
      <span class="font-semibold">{{ value.name }}</span>
    </AugerLink>
    <span v-if="showIcon" class="icon">
      <font-awesome-icon :icon="icon" />
    </span>
    <button v-if="!readonly" class="delete" :class="deleteClass" @click="handleDelete"></button>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { FALLBACK_RECORD_COLOR, FALLBACK_RECORD_ICON, withAppearance } from "@auger/core/fields"
import AugerLink from "../link/AugerLink.vue"
import { recordTarget } from "../link/registry"

interface Props {
  value: Record<string, any>
  readonly?: boolean
  size?: string
  showIcon?: boolean
  fixed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  size: "normal",
  showIcon: true,
  fixed: false,
})

const emit = defineEmits<{
  remove: [record: Record<string, any>]
}>()

// A record from a field's value already carries these; a record from anywhere
// else (a store, a search) gets them from the catalog here.
const appearance = computed(() => withAppearance(props.value))
const icon = computed(() => appearance.value?.icon ?? FALLBACK_RECORD_ICON)
const color = computed(() => appearance.value?.color ?? FALLBACK_RECORD_COLOR)

const tagClass = computed(() => ({
  tag: true,
  "link-tag": true,
  [`is-${props.size}`]: true,
  "is-link": true,
}))

const deleteClass = computed(() => ({
  [`is-${props.size}`]: props.size !== "normal",
}))

const textClass = computed(() => {
  const sizeMap: Record<string, string> = {
    "x-small": "", // takes the chip's own font size
    small: "text-7",
    normal: "text-6",
    medium: "text-5",
    large: "text-4",
  }
  return sizeMap[props.size] ?? "text-6"
})

function handleDelete() {
  emit("remove", props.value)
}
</script>
